import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import HTMLComment from 'react-html-comment';
import { removeAllChildren } from './removeAllChildren';
import { WebBlocksApiComponentT } from '../../lib/REST/types';

/**
 *
 * @example https://beta.reactjs.org/reference/react-dom/createPortal#rendering-react-components-into-non-react-server-markup
 */

export function Portal({
	portalContainer,
	component,
	selector = '',
	getCommentMessage = (prevHTML: string) => prevHTML,
}: {
	selector: string;
	portalContainer: HTMLElement;
	component: WebBlocksApiComponentT;
	getCommentMessage: (prevHTML: string) => string;
}) {
	const [portalEmptyContainer, setPortalEmptyContainer] = useState<
		Element | DocumentFragment | null
	>(null);
	const [prevHTML, setPrevHTML] = useState<string>('');

	useEffect(() => {
		const isContainerCleared = portalContainer === portalEmptyContainer;
		const prevContent = portalContainer?.outerHTML;

		if (isContainerCleared) return;

		// NOTE: Dom operation: removes all previous html content in existing dom node
		const emptyDomNode = removeAllChildren(portalContainer);

		setPortalEmptyContainer(emptyDomNode);
		setPrevHTML(prevContent);
	}, [portalContainer]);

	if (!portalEmptyContainer) {
		return null;
	}

	const portalChild = (
		<>
			<HTMLComment text={getCommentMessage(prevHTML)} />
			{component}
		</>
	);

	return createPortal(portalChild, portalEmptyContainer, selector);
}
